import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Clock, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  Code, FileText, Send, Eye, EyeOff, Shield, AlertCircle,
  Maximize, X, BookOpen
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { toast } from 'sonner';
import useTestScreenLock from '../hooks/useTestScreenLock';

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration for auto-submit threshold
const MAX_VIOLATIONS_BEFORE_AUTO_SUBMIT = 3;

// Violation Warning Modal
const ViolationWarningModal = ({ 
  isOpen, 
  violationType, 
  violationCount, 
  maxViolations, 
  onAcknowledge, 
  onReturnToFullscreen 
}) => {
  if (!isOpen) return null;

  const remainingChances = maxViolations - violationCount;
  const isLastWarning = remainingChances <= 1;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]" data-testid="violation-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className={`p-4 ${isLastWarning ? 'bg-red-600' : 'bg-amber-500'}`}>
          <div className="flex items-center gap-3 text-white">
            <AlertTriangle size={28} />
            <h2 className="text-xl font-bold">
              {isLastWarning ? 'Final Warning!' : 'Proctoring Violation Detected'}
            </h2>
          </div>
        </div>
        
        <div className="p-6">
          <div className="text-center mb-6">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
              isLastWarning ? 'bg-red-100' : 'bg-amber-100'
            }`}>
              <AlertTriangle className={isLastWarning ? 'text-red-600' : 'text-amber-600'} size={40} />
            </div>
            
            <p className="text-gray-700 mb-2">
              {violationType === 'fullscreen_exit' && 'You exited full screen mode!'}
              {violationType === 'tab_switch' && 'You switched to another tab or window!'}
              {violationType === 'esc_key' && 'You pressed the ESC key!'}
              {violationType === 'window_blur' && 'You minimized or switched the window!'}
            </p>
            
            <div className={`inline-block px-4 py-2 rounded-full font-bold ${
              isLastWarning ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
            }`}>
              Violations: {violationCount} / {maxViolations}
            </div>
            
            {isLastWarning ? (
              <p className="mt-4 text-red-600 font-semibold">
                ⚠️ One more violation will auto-submit your assessment!
              </p>
            ) : (
              <p className="mt-4 text-gray-600">
                You have {remainingChances} chance{remainingChances > 1 ? 's' : ''} remaining before auto-submission.
              </p>
            )}
          </div>
          
          <div className="space-y-3">
            <button
              onClick={onReturnToFullscreen}
              className={`w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 ${
                isLastWarning 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
              data-testid="return-fullscreen-btn"
            >
              <Maximize size={18} />
              Return to Full Screen & Continue
            </button>
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-4">
            Stay in full screen mode to avoid further violations.
          </p>
        </div>
      </div>
    </div>
  );
};

// Enhanced Proctoring component with fullscreen enforcement
const ProctoringWrapper = ({ 
  children, 
  onViolation, 
  onAutoSubmit,
  enabled, 
  allowCopyPaste,
  isTestActive,
  violationCount,
  maxViolations
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [currentViolationType, setCurrentViolationType] = useState(null);
  const isFullscreenRef = useRef(false);
  const violationProcessingRef = useRef(false);

  // Enter fullscreen
  const enterFullscreen = useCallback(async () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      isFullscreenRef.current = true;
      return true;
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
      toast.error('Please allow fullscreen mode to continue the assessment');
      return false;
    }
  }, []);

  // Check if currently in fullscreen
  const isInFullscreen = useCallback(() => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    );
  }, []);

  // Handle violation
  const handleViolation = useCallback((type) => {
    if (!enabled || !isTestActive || violationProcessingRef.current) return;
    
    violationProcessingRef.current = true;
    
    const violation = { type, timestamp: new Date().toISOString() };
    onViolation(violation);
    
    setCurrentViolationType(type);
    setShowWarning(true);
    
    // Check if should auto-submit
    const newCount = violationCount + 1;
    if (newCount >= maxViolations) {
      setTimeout(() => {
        toast.error('Maximum violations reached. Auto-submitting assessment...');
        onAutoSubmit();
      }, 1500);
    }
    
    setTimeout(() => {
      violationProcessingRef.current = false;
    }, 1000);
  }, [enabled, isTestActive, onViolation, violationCount, maxViolations, onAutoSubmit]);

  // Handle return to fullscreen
  const handleReturnToFullscreen = useCallback(async () => {
    setShowWarning(false);
    await enterFullscreen();
  }, [enterFullscreen]);

  useEffect(() => {
    if (!enabled || !isTestActive) return;

    // Fullscreen change handler
    const handleFullscreenChange = () => {
      const inFullscreen = isInFullscreen();
      
      if (isFullscreenRef.current && !inFullscreen) {
        // User exited fullscreen
        handleViolation('fullscreen_exit');
      }
      
      isFullscreenRef.current = inFullscreen;
    };

    // Visibility change handler (tab switch)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleViolation('tab_switch');
      }
    };

    // Window blur handler (minimize/switch)
    const handleBlur = () => {
      // Small delay to avoid false positives
      setTimeout(() => {
        if (!document.hasFocus()) {
          handleViolation('window_blur');
        }
      }, 100);
    };

    // ESC key handler
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Don't trigger violation for ESC if modal is showing
        if (!showWarning) {
          handleViolation('esc_key');
        }
        return false;
      }
    };

    // Copy/paste handlers
    const handleCopy = (e) => {
      if (!allowCopyPaste) {
        e.preventDefault();
        toast.error('Copy is disabled during this assessment');
      }
    };

    const handlePaste = (e) => {
      if (!allowCopyPaste) {
        e.preventDefault();
        toast.error('Paste is disabled during this assessment');
      }
    };

    const handleContextMenu = (e) => {
      if (!allowCopyPaste) {
        e.preventDefault();
      }
    };

    // Prevent F11 and other fullscreen toggle keys
    const handleKeyDownGlobal = (e) => {
      if (e.key === 'F11') {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keydown', handleKeyDownGlobal);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keydown', handleKeyDownGlobal);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [enabled, isTestActive, allowCopyPaste, handleViolation, isInFullscreen, showWarning]);

  return (
    <>
      {children}
      <ViolationWarningModal
        isOpen={showWarning && violationCount < maxViolations}
        violationType={currentViolationType}
        violationCount={violationCount}
        maxViolations={maxViolations}
        onAcknowledge={() => setShowWarning(false)}
        onReturnToFullscreen={handleReturnToFullscreen}
      />
    </>
  );
};

const CandidateTestPage = () => {
  const { id: assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const codeFromUrl = searchParams.get('code');
  
  const [stage, setStage] = useState('loading'); // loading, register, instructions, test, submitted, error
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [testData, setTestData] = useState(null);
  
  // Registration form - now includes access code entry
  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '', accessCode: codeFromUrl || '' });
  const [registering, setRegistering] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  // Test state
  const [currentSection, setCurrentSection] = useState('mcq'); // mcq, coding
  const [currentMCQIndex, setCurrentMCQIndex] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [currentCodingIndex, setCurrentCodingIndex] = useState(0);
  const [codeByProblem, setCodeByProblem] = useState({});
  const [codingSubmissions, setCodingSubmissions] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  
  // Timer
  const [timeLeft, setTimeLeft] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  
  // Proctoring - Enhanced
  const [tabSwitches, setTabSwitches] = useState(0);
  const [violations, setViolations] = useState([]);
  const [violationCount, setViolationCount] = useState(0);
  const [isTestActive, setIsTestActive] = useState(false);
  
  // Submitting
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // Lock test screen when test is active
  const shouldLockScreen = stage === 'test' && isTestActive;
  useTestScreenLock(shouldLockScreen);

  // Enter fullscreen helper
  const enterFullscreen = useCallback(async () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      return true;
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
      toast.error('Please allow fullscreen mode to continue');
      return false;
    }
  }, []);

  // Exit fullscreen helper
  const exitFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    } catch (err) {
      // Silently ignore fullscreen exit errors
      console.log('Fullscreen exit skipped');
    }
  }, []);

  useEffect(() => {
    // If code is in URL (legacy links), pre-fill it and verify
    if (codeFromUrl) {
      setRegForm(prev => ({ ...prev, accessCode: codeFromUrl }));
      fetchAssessmentInfo(codeFromUrl);
    } else {
      // New flow: Show registration form where candidate enters code
      setStage('register');
    }
  }, [assessmentId, codeFromUrl]);

  // Timer countdown
  useEffect(() => {
    if (stage !== 'test' || !startedAt) return;
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
      const duration = testData?.assessment?.duration_minutes * 60 || 3600;
      const remaining = Math.max(0, duration - elapsed);
      setTimeLeft(remaining);
      
      if (remaining <= 0) {
        handleSubmit();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [stage, startedAt, testData]);

  const fetchAssessmentInfo = async (accessCode) => {
    if (!accessCode) return;
    
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/api/assessment/${assessmentId}/info?code=${accessCode}`);
      setAssessmentInfo(res.data);
      
      if (res.data.candidate_email) {
        setRegForm(prev => ({ ...prev, email: res.data.candidate_email }));
      }
      
      if (res.data.candidate_status === 'registered' || res.data.candidate_status === 'started') {
        setStage('register');
      } else if (res.data.candidate_status === 'invited') {
        setStage('register');
      } else if (res.data.is_self_registration) {
        // Self-registration not allowed in new flow
        setAssessmentInfo({ 
          error: 'You are not authorized to take this assessment. Please contact the company to get invited.',
          isNotAuthorized: true
        });
        setStage('error');
      } else {
        setStage('register');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load assessment';
      const statusCode = err.response?.status;
      
      setAssessmentInfo({ 
        error: errorMessage, 
        statusCode,
        isExpired: statusCode === 400 && errorMessage.includes('expired'),
        isInactive: statusCode === 400 && errorMessage.includes('no longer active'),
        isInvalidCode: statusCode === 403,
        isNotFound: statusCode === 404,
        isNotAuthorized: statusCode === 403 && errorMessage.includes('not authorized')
      });
      setStage('error');
    } finally {
      setVerifying(false);
    }
  };

  // Verify access code and email before registration
  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    
    // Use codeFromUrl as fallback if regForm.accessCode is empty
    const accessCode = regForm.accessCode.trim() || codeFromUrl || '';
    
    if (!accessCode) {
      toast.error('Please enter the access code');
      return;
    }
    if (!regForm.name.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!regForm.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    setRegistering(true);
    try {
      // First verify the access code and check if email is authorized
      const verifyRes = await axios.post(`${API}/api/assessment/${assessmentId}/verify-access`, {
        access_code: accessCode,
        email: regForm.email.trim().toLowerCase()
      });
      
      if (!verifyRes.data.authorized) {
        toast.error('You are not authorized to take this assessment. Please contact the company.');
        setAssessmentInfo({ 
          error: 'You are not authorized to take this assessment. Only pre-approved candidates can access this test.',
          isNotAuthorized: true
        });
        setStage('error');
        return;
      }
      
      // If authorized, proceed with registration
      const res = await axios.post(`${API}/api/assessment/${assessmentId}/register?code=${accessCode}`, {
        name: regForm.name,
        email: regForm.email.trim().toLowerCase(),
        phone: regForm.phone
      });
      
      setSessionToken(res.data.session_token);
      setAssessmentInfo(verifyRes.data);
      setStage('instructions');
      toast.success('Registration successful!');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Verification failed';
      if (errorMessage.includes('not authorized') || errorMessage.includes('not found') || errorMessage.includes('Invalid')) {
        toast.error('You are not authorized to take this assessment. Please check your email and access code.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setRegistering(false);
    }
  };

  const startTest = async () => {
    try {
      // Enter fullscreen mode first (for proctored assessments)
      if (assessmentInfo?.assessment?.is_proctored) {
        const fullscreenSuccess = await enterFullscreen();
        if (!fullscreenSuccess) {
          toast.error('Full screen mode is required for this proctored assessment');
          return;
        }
      }
      
      const res = await axios.post(`${API}/api/assessment/${assessmentId}/start`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setTestData(res.data);
      setStartedAt(res.data.started_at);
      setTimeLeft(res.data.assessment.duration_minutes * 60);
      
      // Initialize code for each problem
      const initialCode = {};
      res.data.coding_problems?.forEach(p => {
        initialCode[p.id] = p.starter_code?.[selectedLanguage] || getDefaultCode(selectedLanguage);
      });
      setCodeByProblem(initialCode);
      
      // Mark test as active for proctoring
      setIsTestActive(true);
      setViolationCount(0);
      setViolations([]);
      
      setStage('test');
      toast.success('Assessment started! Stay in full screen mode.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start test');
    }
  };

  const getDefaultCode = (lang) => {
    const defaults = {
      python: '# Write your solution here\n\ndef solve():\n    pass\n',
      javascript: '// Write your solution here\n\nfunction solve() {\n    \n}\n',
      cpp: '// Write your solution here\n#include <iostream>\nusing namespace std;\n\nint main() {\n    \n    return 0;\n}\n',
      java: '// Write your solution here\npublic class Solution {\n    public static void main(String[] args) {\n        \n    }\n}\n'
    };
    return defaults[lang] || '';
  };

  const handleMCQAnswer = (mcqId, optionId) => {
    setMcqAnswers(prev => ({ ...prev, [mcqId]: optionId }));
  };

  const handleCodeChange = (problemId, value) => {
    setCodeByProblem(prev => ({ ...prev, [problemId]: value }));
  };

  const runCode = async (problemId) => {
    // This would submit to the coding platform's execution endpoint
    // For now, we'll just mark it as submitted
    toast.info('Code saved. Submit assessment to evaluate.');
  };

  const handleViolation = async (violation) => {
    setViolations(prev => [...prev, violation]);
    setViolationCount(prev => prev + 1);
    
    if (violation.type === 'tab_switch') {
      setTabSwitches(prev => prev + 1);
    }
    
    // Log to backend
    try {
      await axios.post(`${API}/api/assessment/${assessmentId}/proctoring-event`, violation, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
    } catch (err) {
      console.log('Failed to log proctoring event');
    }
  };

  // Auto-submit triggered by proctoring
  const handleAutoSubmit = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const mcqAnswersList = Object.entries(mcqAnswers).map(([mcq_id, selected_option_id]) => ({
        mcq_id,
        selected_option_id
      }));
      
      const res = await axios.post(`${API}/api/assessment/${assessmentId}/submit`, {
        mcq_answers: mcqAnswersList,
        coding_submissions: codingSubmissions,
        tab_switches: tabSwitches,
        proctoring_violations: [...violations, { type: 'auto_submit', reason: 'max_violations_reached', timestamp: new Date().toISOString() }],
        auto_submitted: true
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Exit fullscreen after submission
      setIsTestActive(false);
      exitFullscreen();
      
      setResults(res.data.results);
      setStage('submitted');
      toast.warning('Assessment auto-submitted due to proctoring violations');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    
    const confirmed = window.confirm('Are you sure you want to submit? You cannot change answers after submission.');
    if (!confirmed) return;
    
    setSubmitting(true);
    try {
      const mcqAnswersList = Object.entries(mcqAnswers).map(([mcq_id, selected_option_id]) => ({
        mcq_id,
        selected_option_id
      }));
      
      const res = await axios.post(`${API}/api/assessment/${assessmentId}/submit`, {
        mcq_answers: mcqAnswersList,
        coding_submissions: codingSubmissions,
        tab_switches: tabSwitches,
        proctoring_violations: violations
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Exit fullscreen after submission
      setIsTestActive(false);
      exitFullscreen();
      
      setResults(res.data.results);
      setStage('submitted');
      toast.success('Assessment submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (stage === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (stage === 'error') {
    const errorInfo = assessmentInfo || {};
    
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 overflow-y-auto">
        <div className="flex items-center justify-center min-h-full">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${
              errorInfo.isExpired ? 'text-amber-500' : 
              errorInfo.isNotFound ? 'text-gray-500' : 
              errorInfo.isNotAuthorized ? 'text-red-500' :
              'text-red-500'
            }`} />
          
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {errorInfo.isExpired ? 'Invitation Expired' :
             errorInfo.isInactive ? 'Assessment Closed' :
             errorInfo.isNotAuthorized ? 'Not Authorized' :
             errorInfo.isInvalidCode ? 'Invalid Access Code' :
             errorInfo.isNotFound ? 'Assessment Not Found' :
             'Access Error'}
          </h2>
          
          <p className="text-gray-600 mb-6">
            {errorInfo.error || 'The assessment link may be invalid or expired.'}
          </p>
          
          <div className="space-y-3">
            {errorInfo.isExpired && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Please contact the company to request a new assessment link.
              </p>
            )}
            
            {errorInfo.isNotAuthorized && (
              <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                Only pre-approved candidates can access this assessment. Please contact the company to get invited.
              </p>
            )}
            
            {errorInfo.isInvalidCode && !errorInfo.isNotAuthorized && (
              <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                Make sure you're using the correct access code and email provided to the company.
              </p>
            )}
            
            <button
              onClick={() => {
                setStage('register');
                setAssessmentInfo(null);
                setRegForm({ name: '', email: '', phone: '', accessCode: '' });
              }}
              className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Registration stage
  if (stage === 'register') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 py-8 px-4 overflow-y-auto">
        <div className="flex items-center justify-center min-h-full">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="text-purple-600" size={28} />
              </div>
              {assessmentInfo?.assessment ? (
                <>
                  <h1 className="text-2xl font-bold text-gray-900">{assessmentInfo.assessment.title}</h1>
                  <p className="text-gray-500 mt-2">by {assessmentInfo.assessment.company_name}</p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-gray-900">Assessment Registration</h1>
                  <p className="text-gray-500 mt-2">Enter your details to begin</p>
                </>
              )}
            </div>

          {assessmentInfo?.assessment && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Duration</p>
                  <p className="font-medium text-gray-900">{assessmentInfo.assessment.duration_minutes} minutes</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Points</p>
                  <p className="font-medium text-gray-900">{assessmentInfo.assessment.total_points}</p>
                </div>
                <div>
                  <p className="text-gray-500">MCQs</p>
                  <p className="font-medium text-gray-900">{assessmentInfo.assessment.total_mcqs}</p>
                </div>
                <div>
                  <p className="text-gray-500">Coding</p>
                  <p className="font-medium text-gray-900">{assessmentInfo.assessment.total_coding_problems}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleVerifyAndRegister} className="space-y-4">
            {/* Access Code Field - only show if not pre-filled from URL */}
            {!codeFromUrl && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Code *</label>
                <input
                  type="text"
                  required
                  value={regForm.accessCode}
                  onChange={(e) => setRegForm(prev => ({ ...prev, accessCode: e.target.value.toUpperCase() }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-lg tracking-widest text-center uppercase"
                  placeholder="Enter access code"
                  data-testid="candidate-access-code-input"
                  maxLength={12}
                />
                <p className="text-xs text-gray-500 mt-1">Enter the access code shared by the company</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                required
                value={regForm.name}
                onChange={(e) => setRegForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter your full name"
                data-testid="candidate-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email ID *</label>
              <input
                type="email"
                required
                value={regForm.email}
                onChange={(e) => setRegForm(prev => ({ ...prev, email: e.target.value }))}
                className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${assessmentInfo?.candidate_email ? 'bg-gray-50' : ''}`}
                placeholder="Enter your email"
                readOnly={!!assessmentInfo?.candidate_email}
                data-testid="candidate-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">Use the email address provided to the company</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
              <input
                type="tel"
                value={regForm.phone}
                onChange={(e) => setRegForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Your phone number"
              />
            </div>

            <button
              type="submit"
              disabled={registering || verifying}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="register-submit-btn"
            >
              {registering || verifying ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                'Continue to Assessment'
              )}
            </button>
          </form>
          
          <p className="text-xs text-gray-500 text-center mt-6">
            Only pre-approved candidates can access this assessment
          </p>
        </div>
        </div>
      </div>
    );
  }

  // Instructions stage
  if (stage === 'instructions') {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-purple-50 to-indigo-50 py-6 px-4">
        <div className="flex items-start justify-center">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 md:p-8 mb-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Before You Begin</h1>
              <p className="text-gray-500 mt-2">Please read the instructions carefully</p>
            </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
              <Clock className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-amber-900">Time Limit</p>
                <p className="text-sm text-amber-700">You have {assessmentInfo?.assessment?.duration_minutes} minutes to complete this assessment. Timer starts when you click "Start Assessment".</p>
              </div>
            </div>

            {assessmentInfo?.assessment?.is_proctored && (
              <>
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg">
                  <Shield className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium text-red-900">Proctoring Enabled</p>
                    <p className="text-sm text-red-700">This assessment is proctored. The following activities will be monitored:</p>
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside space-y-1">
                      <li>Tab switching or window changes</li>
                      <li>Exiting full screen mode</li>
                      <li>Pressing ESC key</li>
                      <li>Minimizing the browser</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                  <Maximize className="text-purple-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium text-purple-900">Full Screen Mode Required</p>
                    <p className="text-sm text-purple-700">
                      The browser will enter full screen mode when you start. 
                      <strong> You have {MAX_VIOLATIONS_BEFORE_AUTO_SUBMIT} chances</strong> - after that, your assessment will be auto-submitted.
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <FileText className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-blue-900">Assessment Structure</p>
                <p className="text-sm text-blue-700">
                  {assessmentInfo?.assessment?.total_mcqs} Multiple Choice Questions • 
                  {assessmentInfo?.assessment?.total_coding_problems} Coding Problems • 
                  {assessmentInfo?.assessment?.total_points} Total Points
                </p>
              </div>
            </div>

            {assessmentInfo?.assessment?.instructions && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 mb-2">Additional Instructions</p>
                <p className="text-sm text-gray-600">{assessmentInfo.assessment.instructions}</p>
              </div>
            )}
          </div>

          <button
            onClick={startTest}
            className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 text-lg"
            data-testid="start-test-btn"
          >
            {assessmentInfo?.assessment?.is_proctored ? (
              <>
                <Maximize size={20} />
                Enter Full Screen & Start Assessment
              </>
            ) : (
              <>
                Start Assessment
                <ChevronRight size={20} />
              </>
            )}
          </button>
        </div>
        </div>
      </div>
    );
  }

  // Test stage
  if (stage === 'test' && testData) {
    const mcqs = testData.mcqs || [];
    const codingProblems = testData.coding_problems || [];
    const currentMCQ = mcqs[currentMCQIndex];
    const currentProblem = codingProblems[currentCodingIndex];

    return (
      <ProctoringWrapper 
        enabled={testData.assessment.is_proctored}
        allowCopyPaste={testData.assessment.allow_copy_paste}
        onViolation={handleViolation}
        onAutoSubmit={handleAutoSubmit}
        isTestActive={isTestActive}
        violationCount={violationCount}
        maxViolations={MAX_VIOLATIONS_BEFORE_AUTO_SUBMIT}
      >
        <div className={`min-h-screen bg-gray-100 flex flex-col ${shouldLockScreen ? 'test-screen-container' : ''}`} data-testid="candidate-test-page">
          {/* Minimal Locked Header */}
          <header data-test-header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center gap-4">
              {/* EvalentX Logo - Minimal */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <BookOpen size={18} className="text-white" />
                </div>
                <span className="text-lg font-bold text-purple-600 hidden sm:inline">EvalentX</span>
              </div>
              <div className="w-px h-6 bg-gray-200" />
              <h1 className="font-bold text-gray-900">{testData.assessment.title}</h1>
              {violationCount > 0 && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  violationCount >= MAX_VIOLATIONS_BEFORE_AUTO_SUBMIT - 1 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  ⚠️ {violationCount}/{MAX_VIOLATIONS_BEFORE_AUTO_SUBMIT} violations
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono font-medium ${
                timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
              }`}>
                <Clock size={16} />
                {formatTime(timeLeft)}
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="submit-assessment-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Assessment'}
              </button>
            </div>
          </header>

          {/* Section Tabs */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex gap-2">
            <button
              onClick={() => setCurrentSection('mcq')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentSection === 'mcq' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              MCQs ({Object.keys(mcqAnswers).length}/{mcqs.length})
            </button>
            <button
              onClick={() => setCurrentSection('coding')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentSection === 'coding' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Coding ({codingProblems.length})
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* MCQ Section */}
            {currentSection === 'mcq' && currentMCQ && (
              <div className="min-h-full flex flex-col md:flex-row">
                {/* Question List Sidebar */}
                <div className="w-20 bg-white border-r border-gray-200 p-2 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1">
                    {mcqs.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentMCQIndex(idx)}
                        className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                          currentMCQIndex === idx
                            ? 'bg-purple-600 text-white'
                            : mcqAnswers[mcqs[idx]?.id]
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Question Content */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <div className="max-w-3xl mx-auto">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-500">Question {currentMCQIndex + 1} of {mcqs.length}</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium">
                        {currentMCQ.points} points
                      </span>
                    </div>
                    
                    <h2 className="text-xl font-medium text-gray-900 mb-6">{currentMCQ.question}</h2>
                    
                    <div className="space-y-3">
                      {currentMCQ.options.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleMCQAnswer(currentMCQ.id, option.id)}
                          className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                            mcqAnswers[currentMCQ.id] === option.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              mcqAnswers[currentMCQ.id] === option.id
                                ? 'border-purple-500 bg-purple-500'
                                : 'border-gray-300'
                            }`}>
                              {mcqAnswers[currentMCQ.id] === option.id && (
                                <div className="w-2 h-2 bg-white rounded-full" />
                              )}
                            </div>
                            <span className="text-gray-900">{option.text}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="flex justify-between mt-8">
                      <button
                        onClick={() => setCurrentMCQIndex(Math.max(0, currentMCQIndex - 1))}
                        disabled={currentMCQIndex === 0}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        <ChevronLeft size={18} /> Previous
                      </button>
                      <button
                        onClick={() => {
                          if (currentMCQIndex < mcqs.length - 1) {
                            setCurrentMCQIndex(currentMCQIndex + 1);
                          } else {
                            setCurrentSection('coding');
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        {currentMCQIndex < mcqs.length - 1 ? 'Next' : 'Go to Coding'} <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Coding Section */}
            {currentSection === 'coding' && (
              <div className="min-h-full flex flex-col md:flex-row">
                {/* Problem List Sidebar */}
                <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Problems</h3>
                    <div className="space-y-2">
                      {codingProblems.map((problem, idx) => (
                        <button
                          key={problem.id}
                          onClick={() => setCurrentCodingIndex(idx)}
                          className={`w-full p-3 text-left rounded-lg transition-colors ${
                            currentCodingIndex === idx
                              ? 'bg-purple-100 border-purple-300 border'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <p className="font-medium text-gray-900 text-sm">{problem.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              problem.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                              problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {problem.difficulty}
                            </span>
                            <span className="text-xs text-gray-500">{problem.points} pts</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Problem Content */}
                {currentProblem && (
                  <div className="flex-1 flex">
                    {/* Problem Description */}
                    <div className="w-1/2 p-6 overflow-y-auto border-r border-gray-200">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">{currentProblem.title}</h2>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700 whitespace-pre-wrap">{currentProblem.description}</p>
                        
                        {currentProblem.constraints && (
                          <div className="mt-4">
                            <h4 className="font-medium text-gray-900">Constraints:</h4>
                            <pre className="bg-gray-100 p-3 rounded text-sm">{currentProblem.constraints}</pre>
                          </div>
                        )}
                        
                        {currentProblem.test_cases?.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium text-gray-900">Examples:</h4>
                            {currentProblem.test_cases.map((tc, idx) => (
                              <div key={idx} className="bg-gray-50 p-3 rounded mt-2">
                                <p className="text-sm"><strong>Input:</strong></p>
                                <pre className="bg-white p-2 rounded text-xs">{tc.input}</pre>
                                <p className="text-sm mt-2"><strong>Output:</strong></p>
                                <pre className="bg-white p-2 rounded text-xs">{tc.expected_output}</pre>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Code Editor */}
                    <div className="w-1/2 flex flex-col">
                      <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-white">
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          className="px-3 py-1 border border-gray-200 rounded text-sm"
                        >
                          <option value="python">Python</option>
                          <option value="javascript">JavaScript</option>
                          <option value="cpp">C++</option>
                          <option value="java">Java</option>
                        </select>
                        <button
                          onClick={() => runCode(currentProblem.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Run Code
                        </button>
                      </div>
                      <div className="flex-1">
                        <Editor
                          height="100%"
                          language={selectedLanguage}
                          value={codeByProblem[currentProblem.id] || getDefaultCode(selectedLanguage)}
                          onChange={(value) => handleCodeChange(currentProblem.id, value || '')}
                          theme="vs-dark"
                          options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            lineNumbers: 'on',
                            automaticLayout: true
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ProctoringWrapper>
    );
  }

  // Submitted stage
  if (stage === 'submitted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Assessment Submitted!</h1>
          <p className="text-gray-500 mb-6">Thank you for completing the assessment.</p>
          
          {results && (
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="text-4xl font-bold text-purple-600 mb-2">{results.percentage}%</div>
              <p className="text-gray-600">
                Score: {results.score}/{results.total_points}
              </p>
              <div className={`mt-4 px-4 py-2 rounded-full inline-block font-medium ${
                results.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {results.passed ? 'PASSED' : 'NOT PASSED'}
              </div>
              <div className="mt-4 text-sm text-gray-500">
                Time taken: {Math.round(results.time_taken_seconds / 60)} minutes
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-400">
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default CandidateTestPage;
